import { embalagemDoProduto, temEmbalagemFechada, unidadeDoPeso } from './embalagem'

// O trecho "N x ... x PESO" do nome: um ou mais "N x", depois numero + unidade
// de PESO ou VOLUME. Sem a unidade nao reconhece -- e isso que protege as 1.057
// medidas de forma ("Forma Redonda 25 x 5") medidas em 23/09/2026. As fronteiras
// (?<!...) e (?!...) impedem casar dentro de palavra ("300GRAMAS").
const PADRAO = /(?<![\p{L}\d])(?:\d+\s*[xX]\s*)+(\d+(?:[.,]\d+)?)\s*(KG|GR|G|ML|L)(?![\p{L}\d])/iu

export interface TituloDoProduto {
  titulo: string
  pesoDaUnidade: string | null
}

// ⚠ SO para a ficha (decisao dela, 23/09). Pedido, PDF, busca e mensagem ao
// cliente continuam com a descricao completa -- e como a fabrica chama.
export function separarTitulo(descricao: string): TituloDoProduto {
  const inteira = descricao.replace(/\s+/g, ' ').trim()
  const m = PADRAO.exec(inteira)
  if (!m) return { titulo: inteira, pesoDaUnidade: null }
  const pesoDaUnidade = `${m[1]}${unidadeDoPeso(m[2])}`
  const titulo = `${inteira.slice(0, m.index)} ${inteira.slice(m.index + m[0].length)}`
    .replace(/\s+/g, ' ')
    .trim()
  return { titulo: titulo === '' ? inteira : titulo, pesoDaUnidade }
}

// "UND 300G · CX C/ 12 UND". A QUANTIDADE vem sempre do cadastro, pelo funil
// de embalagem.ts -- nunca do nome (o nome "2X12X30ML" diz 2 e 12; o cadastro
// diz 24, e e o cadastro que o pedido usa).
export function textoDaEmbalagemNaFicha(produto: {
  descricao: string
  unidade?: string | null
  unidades_por_embalagem?: number | null
}): string {
  const { pesoDaUnidade } = separarTitulo(produto.descricao)
  const unidade = (produto.unidade ?? '').trim()
  if (temEmbalagemFechada(produto)) {
    const { fator } = embalagemDoProduto(produto)
    const caixa = `${unidade || 'CX'} C/ ${fator} UND`
    return pesoDaUnidade ? `UND ${pesoDaUnidade} · ${caixa}` : caixa
  }
  if (pesoDaUnidade) return unidade ? `${unidade} ${pesoDaUnidade}` : pesoDaUnidade
  return unidade || '—'
}

// Um peso SOZINHO no nome ("COBERTURA SORVETE CHOCOLATE  -  190g"). So conta
// quando ha EXATAMENTE um: com dois ("KIT 500g + 200g") nao da para saber qual
// e o da unidade, e nao adivinhamos. Medido em 23/09: numa amostra de 25 dos
// 331 produtos com caixa assim, o peso sozinho era sempre o da unidade.
const PESO_SOZINHO = /(?<![\p{L}\d])(\d+(?:[.,]\d+)?)\s*(KG|GR|G|ML|L)(?![\p{L}\d])/giu

function pesoSozinhoNoNome(descricao: string): string | null {
  const achados = [...descricao.matchAll(PESO_SOZINHO)]
  if (achados.length !== 1) return null
  const [, numero, unidade] = achados[0]
  return `${numero}${unidadeDoPeso(unidade)}`
}

// O titulo da TELA da ficha (decisao dela, 24/09: "nessa tela pode tirar peso
// do titulo" -- o peso ja aparece no card Embalagem). Tira o "12X300G" ou, sem
// ele, o peso SOZINHO com o traco que o separa ("... - 190g"). Mesma cautela do
// card: com dois pesos nao mexe. Pedido, PDF e mensagem seguem com o nome inteiro.
export function tituloDaFicha(descricao: string): string {
  const { titulo, pesoDaUnidade } = separarTitulo(descricao)
  if (pesoDaUnidade) return titulo
  const achados = [...titulo.matchAll(PESO_SOZINHO)]
  if (achados.length !== 1) return titulo
  const m = achados[0]
  const antes = titulo.slice(0, m.index).replace(/\s*[-–—/]\s*$/, '')
  const depois = titulo.slice(m.index! + m[0].length)
  const limpo = `${antes} ${depois}`.replace(/\s+/g, ' ').trim()
  return limpo === '' ? titulo : limpo
}

// A MEDIDA de uma forma ("Forma Redonda 25 x 5"): 2 ou 3 numeros separados por
// "x", SEM unidade de peso (com unidade seria o "12X300G", que ja e lido acima).
// Na tela da ficha ela faz o papel do peso (roteiro dela, 24/09). So conta com
// EXATAMENTE uma -- mesma cautela do peso sozinho.
const MEDIDA = /(?<![\p{L}\d])(\d+(?:[.,]\d+)?(?:\s*[xX]\s*\d+(?:[.,]\d+)?){1,2})(?![\p{L}\d])/gu

function medidaNoNome(descricao: string): string | null {
  const achados = [...descricao.matchAll(MEDIDA)]
  if (achados.length !== 1) return null
  return achados[0][1].split(/\s*[xX]\s*/).join(' x ')
}

export interface PartesDaEmbalagem {
  principal: string
  detalhe: string | null
}

// A embalagem da TELA da ficha em duas partes (opcao A dela, 23/09 21h, igual
// ao print): "CX" grande e embaixo "Contém 18 unidades de 190G". Mesma regra de
// textoDaEmbalagemNaFicha -- quantidade do cadastro, peso do nome --, so outro
// desenho. A mensagem do Compartilhar continua com a linha unica.
export function partesDaEmbalagemNaFicha(produto: {
  descricao: string
  unidade?: string | null
  unidades_por_embalagem?: number | null
}): PartesDaEmbalagem {
  // O "18X190G" primeiro; sem ele, um peso sozinho no nome (o caso real dela).
  // Sem peso nenhum, a medida da forma (roteiro dela, 24/09).
  const pesoDaUnidade =
    separarTitulo(produto.descricao).pesoDaUnidade ??
    pesoSozinhoNoNome(produto.descricao) ??
    medidaNoNome(produto.descricao)
  // Em MAIUSCULO na tela ("fd" -> "FD", roteiro dela, 24/09).
  const unidade = (produto.unidade ?? '').trim().toUpperCase()
  if (temEmbalagemFechada(produto)) {
    const { fator } = embalagemDoProduto(produto)
    // "cada" (pedido dela, 23/09 21h30): deixa claro que o peso e de UMA unidade.
    const detalhe = `Contém ${fator} unidades${pesoDaUnidade ? ` de ${pesoDaUnidade} cada` : ''}`
    return { principal: unidade || 'CX', detalhe }
  }
  if (pesoDaUnidade) return { principal: unidade || 'UN', detalhe: `De ${pesoDaUnidade}` }
  return { principal: unidade || '—', detalhe: null }
}
